import React from 'react';
import { Card, Row, Col, Button, Typography, Statistic, List, Tag, Carousel } from 'antd';
import { CarOutlined, EnvironmentOutlined, PhoneOutlined, StarOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import rentImg from '../../assets/services/rent.png';
import aiImg from '../../assets/services/ai.png';
import chatterImg from '../../assets/services/chatter.png';
import corporateImage from '../../assets/services/corporation.png';

const { Title, Paragraph } = Typography;

interface HeroButton {
  text: string;
  onClick: () => void;
  type: 'primary' | 'default';
  icon?: React.ReactNode;
  style: React.CSSProperties;
}

interface HeroSlide {
  background: string;
  title: string;
  subtitle: string;
  buttons: HeroButton[];
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const heroSlides: HeroSlide[] = [
    {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      title: 'スマートモビリティ、自由な移動',
      subtitle: 'AES Mobility は、プロフェッショナルで便利、安全なレンタカーと国境を越えた車両サービスを提供いたします',
      buttons: [
        {
          text: '今すぐレンタル',
          onClick: () => navigate('/rental'),
          type: 'primary' as const,
          style: {
            background: 'rgba(255,255,255,0.2)',
            borderColor: 'rgba(255,255,255,0.4)',
          }
        },
        {
          text: '今すぐ配車',
          onClick: () => navigate('/ride'),
          type: 'primary' as const,
          style: {
            background: 'rgba(255,255,255,0.2)',
            borderColor: 'rgba(255,255,255,0.4)',
          }
        },
        {
          text: 'マイページ',
          onClick: () => navigate('/profile'),
          type: 'default' as const,
          style: {
            background: 'transparent',
            borderColor: 'rgba(255,255,255,0.6)',
            color: 'white',
          }
        }
      ]
    },
    {
      background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
      title: '仮想世界での新しい移動体験を始めよう',
      subtitle: 'メタバースワールド',
      buttons: [
        {
          text: 'Play',
          onClick: () => navigate('/metaverse'),
          type: 'primary' as const,
          icon: <PlayCircleOutlined />,
          style: {
            background: 'rgba(255,255,255,0.2)',
            borderColor: 'rgba(255,255,255,0.4)',
            fontSize: 18,
          }
        }
      ]
    },
    {
      background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
      title: '問い合わせ',
      subtitle: 'AESモビリティ株式会社は、システムと小型EV車両を一体で提供し、AI×メタバースによる体験からリアルな移動・集客へつなげます。ご関心のある企業・学校・自治体のご担当者様は、ぜひこちらよりご相談ください',
      buttons: [
        {
          text: '問い合わせ',
          onClick: () => openLink('https://docs.google.com/forms/d/e/1FAIpQLSfR0jDEhAtxHm-DlM4AlI0CpOP3c0otaEPTzzNExJOUMi3Nww/viewform'),
          type: 'primary' as const,
          icon: <PlayCircleOutlined />,
          style: {
            background: 'rgba(255,255,255,0.2)',
            borderColor: 'rgba(255,255,255,0.4)',
            fontSize: 18,
          }
        }
      ]
    }
  ];

  const features = [
    {
      title: '便利な予約',
      description: 'オンライン車両選択・クイック予約、複数支払い方法に対応',
      icon: <CarOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
    },
    {
      title: '全国カバー',
      description: '全国各都市に店舗、近くで車両の受け渡し・返却サービス',
      icon: <EnvironmentOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
    },
    {
      title: '24時間カスタマーサービス',
      description: '年中無休のカスタマーサービス、いつでも問題を解決',
      icon: <PhoneOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
    },
    {
      title: '品質保証',
      description: '厳格な車両点検、安全運転を保証',
      icon: <StarOutlined style={{ fontSize: 24, color: '#eb2f96' }} />,
    },
  ];

  const services = [
    {
      title: 'レンタカー',
      description: '時間レンタル・日レンタル、自由なドライブ',
      price: '¥88/日から',
      tag: '人気',
      color: 'red',
      path: '/rental',
      image: rentImg,
    },
    {
      title: '相乗り',
      description: '国境を越えた相乗りサービス、経済的でお得',
      price: '¥45から',
      tag: '節約',
      color: 'blue',
      path: '/ride',
      image: aiImg,
    },
    {
      title: 'チャーターサービス',
      description: '専用車両、快適で便利',
      price: '¥280から',
      tag: '快適',
      color: 'green',
      path: '/ride',
      image: chatterImg,
    },
    {
      title: '企業サービス',
      description: '企業向け車両、一元管理',
      price: 'カスタム価格',
      tag: '企業',
      color: 'orange',
      path: '/rental',
      image: corporateImage,
    },
  ];

  const recentNews = [
    {
      title: '新エネルギー車100台を新規導入',
      content: '環境保護への取り組みとして、お客様にご利用いただける電気自動車を100台追加いたしました。',
      date: '2024-01-15',
    },
    {
      title: '春節期間のサービス調整のお知らせ',
      content: '春節期間中、一部店舗の営業時間が変更となります。事前にご確認ください。',
      date: '2024-01-10',
    },
    {
      title: '会員優待キャンペーン開始',
      content: '会員登録で初回ご利用10％割引、さらにポイント交換でお得な特典をご用意しております。',
      date: '2024-01-05',
    },
  ];

  return (
    <div style={{ padding: '20px 0' }}>
      <div className="container">
        {/* Hero Carousel */}
        <div style={{ position: 'relative' }}>
          <style>{`
            .ant-carousel .ant-carousel-prev,
            .ant-carousel .ant-carousel-next {
              width: 60px !important;
              height: 60px !important;
              font-size: 24px !important;
              line-height: 60px !important;
              background: rgba(0, 0, 0, 0.3) !important;
              border-radius: 50% !important;
              border: 2px solid rgba(255, 255, 255, 0.6) !important;
            }
            .ant-carousel .ant-carousel-prev:hover,
            .ant-carousel .ant-carousel-next:hover {
              background: rgba(0, 0, 0, 0.6) !important;
              border-color: rgba(255, 255, 255, 0.9) !important;
            }
            .ant-carousel .ant-carousel-dots li {
              width: 16px !important;
              height: 16px !important;
              margin: 0 8px !important;
            }
            .ant-carousel .ant-carousel-dots li button {
              width: 16px !important;
              height: 16px !important;
              border-radius: 50% !important;
              background: rgba(255, 255, 255, 0.4) !important;
              border: 2px solid rgba(255, 255, 255, 0.6) !important;
            }
            .ant-carousel .ant-carousel-dots li.ant-carousel-dot-active button {
              background: rgba(255, 255, 255, 0.9) !important;
              border-color: rgba(255, 255, 255, 1) !important;
            }
          `}</style>
          <Carousel
            autoplay
            autoplaySpeed={4000}
            arrows
            style={{ marginBottom: 40, borderRadius: 12, overflow: 'hidden' }}
          >
          {heroSlides.map((slide, index) => (
            <div key={index}>
              <div
                style={{
                  background: slide.background,
                  padding: '60px 40px',
                  color: 'white',
                  textAlign: 'center',
                  minHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Title level={1} style={{ color: 'white', fontSize: 42, marginBottom: 16 }}>
                    {slide.title}
                  </Title>
                  <Paragraph style={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: 18,
                    marginBottom: 32,
                    maxWidth: 800,
                    margin: '0 auto 32px auto',
                    lineHeight: 1.6
                  }}>
                    {slide.subtitle}
                  </Paragraph>
                </div>
                <Row gutter={16} justify="center">
                  {slide.buttons.map((button, buttonIndex) => (
                    <Col key={buttonIndex}>
                      <Button
                        type={button.type}
                        size="large"
                        icon={button.icon || undefined}
                        onClick={button.onClick}
                        style={{
                          height: 48,
                          fontSize: 16,
                          padding: '0 32px',
                          ...button.style,
                        }}
                      >
                        {button.text}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          ))}
          </Carousel>
        </div>

        {/* Statistics */}
        <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="サービス都市" value={50} suffix="都市" />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="車両総数" value={5000} suffix="台" />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="サービス利用者" value={100000} suffix="+" />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="顧客満足度" value={98} suffix="%" />
            </Card>
          </Col>
        </Row>

        {/* Features */}
        <Card style={{ marginBottom: 40 }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
            なぜ私たちを選ぶのか
          </Title>
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <div style={{ textAlign: 'center', padding: 20 }}>
                  {feature.icon}
                  <Title level={4} style={{ margin: '16px 0 8px 0' }}>
                    {feature.title}
                  </Title>
                  <Paragraph type="secondary">{feature.description}</Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Services */}
        <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
          <Col span={24}>
            <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
              サービス種類
            </Title>
          </Col>
          {services.map((service, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card
                hoverable
                style={{ height: '100%' }}
                cover={
                  <div style={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5'
                  }}>
                    <img
                      alt={service.title}
                      src={service.image}
                      style={{
                        maxHeight: 180,
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                }
                actions={[
                  <Button type="primary" onClick={() => navigate(service.path)}>
                    今すぐ予約
                  </Button>,
                ]}
              >
                <div style={{ position: 'relative' }}>
                  <Tag color={service.color} style={{ position: 'absolute', top: -8, right: -8 }}>
                    {service.tag}
                  </Tag>
                  <Title level={4}>{service.title}</Title>
                  <Paragraph type="secondary">{service.description}</Paragraph>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                    {service.price}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* News */}
        <Card title="最新情報" style={{ marginBottom: 40 }}>
          <List
            itemLayout="vertical"
            dataSource={recentNews}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={item.title}
                  description={item.date}
                />
                <div>{item.content}</div>
              </List.Item>
            )}
          />
        </Card>
      </div>
    </div>
  );
};

export default HomePage;